import { useState, useEffect } from "react";
import { 
  Eye, Monitor, Smartphone, Tablet, Clock, TrendingUp, 
  Users, FileText, ExternalLink, ChevronDown, ChevronUp 
} from "lucide-react";
import { db as supabaseUntyped } from "@/lib/supabaseUntyped";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

interface ShareView {
  id: string;
  viewed_at: string;
  device_type: string | null;
  referrer: string | null;
}

interface DocumentView {
  document_id: string;
  viewed_at: string;
  document_title?: string;
}

interface ShareTrackingStatsProps {
  collectionId?: string;
  shareLinkId?: string;
  className?: string;
}

export function ShareTrackingStats({ 
  collectionId, 
  shareLinkId,
  className = "" 
}: ShareTrackingStatsProps) {
  const [views, setViews] = useState<ShareView[]>([]);
  const [documentViews, setDocumentViews] = useState<DocumentView[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [collectionId, shareLinkId]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Fetch view events
      let query = supabaseUntyped
        .from("share_link_views")
        .select("id, viewed_at, device_type, referrer")
        .order("viewed_at", { ascending: false })
        .limit(100);

      if (collectionId) {
        query = query.eq("collection_id", collectionId);
      } else if (shareLinkId) {
        query = query.eq("share_link_id", shareLinkId);
      } else {
        setLoading(false);
        return;
      }

      const { data: viewsData, error: viewsError } = await query;
      
      if (viewsError) {
        console.error("Error fetching views:", viewsError);
      } else {
        setViews(viewsData || []);
      }

      // Fetch document-level views if collection
      if (collectionId) {
        const { data: docViewsData, error: docViewsError } = await supabaseUntyped
          .from("collection_document_views")
          .select(`
            document_id,
            viewed_at,
            documents:document_id(title)
          `)
          .eq("collection_id", collectionId)
          .order("viewed_at", { ascending: false })
          .limit(50);

        if (!docViewsError && docViewsData) {
          setDocumentViews(docViewsData.map((dv: any) => ({
            document_id: dv.document_id,
            viewed_at: dv.viewed_at,
            document_title: dv.documents?.title
          })));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const totalViews = views.length;
  const uniqueViewers = new Set(views.map(v => v.id)).size; // Approximation
  const deviceCounts = views.reduce((acc, v) => {
    const device = v.device_type || "unknown";
    acc[device] = (acc[device] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const lastView = views[0];
  const viewsToday = views.filter(v => {
    const viewDate = new Date(v.viewed_at);
    const today = new Date();
    return viewDate.toDateString() === today.toDateString();
  }).length;

  const DeviceIcon = ({ type }: { type: string }) => {
    switch (type) {
      case "mobile": return <Smartphone className="w-3.5 h-3.5" />;
      case "tablet": return <Tablet className="w-3.5 h-3.5" />;
      case "desktop": return <Monitor className="w-3.5 h-3.5" />;
      default: return <Monitor className="w-3.5 h-3.5" />;
    }
  };

  if (loading) {
    return (
      <div className={`space-y-2 ${className}`}>
        <Skeleton className="h-4 w-32" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
    );
  }

  if (totalViews === 0) {
    return (
      <div className={`text-center py-4 text-muted-foreground ${className}`}>
        <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Aucune vue pour le moment</p>
        <p className="text-xs">Les statistiques apparaîtront ici</p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Summary stats */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary" className="flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" />
          {totalViews} vue{totalViews > 1 ? "s" : ""}
        </Badge>
        
        {viewsToday > 0 && (
          <Badge variant="default" className="flex items-center gap-1.5 bg-primary/10 text-primary border-primary/30">
            <TrendingUp className="w-3.5 h-3.5" />
            +{viewsToday} aujourd'hui
          </Badge>
        )}
      </div>

      {/* Device breakdown */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(deviceCounts).map(([device, count]) => (
          <div 
            key={device}
            className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded"
          >
            <DeviceIcon type={device} />
            <span className="capitalize">{device}</span>
            <span className="font-medium">{count}</span>
          </div>
        ))}
      </div>

      {/* Last view */}
      {lastView && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          <span>
            Dernière vue {formatDistanceToNow(new Date(lastView.viewed_at), { 
              addSuffix: true, 
              locale: fr 
            })}
          </span>
        </div>
      )}

      {/* Expandable details */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        className="w-full justify-between text-xs h-8"
      >
        <span>Détails des vues</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </Button>

      {expanded && (
        <div className="space-y-3 pt-2 border-t">
          {/* Recent views list */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2">
              Dernières visites
            </h4>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {views.slice(0, 10).map((view) => (
                <div 
                  key={view.id}
                  className="flex items-center justify-between text-xs bg-muted/30 rounded px-2 py-1.5"
                >
                  <div className="flex items-center gap-2">
                    <DeviceIcon type={view.device_type || "unknown"} />
                    <span className="text-muted-foreground">
                      {formatDistanceToNow(new Date(view.viewed_at), { 
                        addSuffix: true, 
                        locale: fr 
                      })}
                    </span>
                  </div>
                  {view.referrer && (
                    <Badge variant="outline" className="text-[10px] h-5">
                      <ExternalLink className="w-2.5 h-2.5 mr-1" />
                      {new URL(view.referrer).hostname.replace("www.", "")}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Document-level views (for collections) */}
          {documentViews.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">
                Documents consultés
              </h4>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {documentViews.slice(0, 8).map((dv, idx) => (
                  <div 
                    key={`${dv.document_id}-${idx}`}
                    className="flex items-center gap-2 text-xs"
                  >
                    <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="truncate flex-1">{dv.document_title || "Document"}</span>
                    <span className="text-muted-foreground text-[10px]">
                      {formatDistanceToNow(new Date(dv.viewed_at), { 
                        addSuffix: true, 
                        locale: fr 
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
